import { SyntheticEvent } from 'react';
import {
  ScheduleFormData,
  getRepeatUnit,
} from 'presentations/views/helpers/routine';
import { DateTime } from 'infras/datetime';

interface Props {
  startedAt: DateTime | null;
  isAllday: boolean;
  schedule: ScheduleFormData[];
  index: number;
  isWeeklySingleSelect?: boolean;
  onSubmit: any;
}

export const useRoutineScheduleForm = (props: Props) => {
  // utcで保存された値を現在のタイムゾーンで表示する
  const schedule: ScheduleFormData | null =
    props.schedule.length > 0 ? props.schedule[0] : null;

  const monthInterval: number = schedule?.monthInterval || 1;
  const dayInterval: number = schedule?.dayInterval || 0;
  const wdayInterval: number = schedule?.wdayInterval || 0;

  const repeatUnit: string = getRepeatUnit(
    monthInterval,
    dayInterval,
    wdayInterval,
  );

  const day: number | null = schedule?.day || null;
  const wday: any[] =
    repeatUnit === 'weekly'
      ? props.schedule.filter((s) => s !== null).map((s) => s.wday)
      : [];
  let hour: number =
    !props.isAllday && typeof props.startedAt?.hour === 'number'
      ? props.startedAt.hour
      : 11;
  if (schedule && schedule.hour !== null) hour = schedule.hour;
  let minute: number = props.startedAt?.minute ?? 0;
  if (schedule && schedule.minute !== null) minute = schedule.minute;

  function onChangeStartedAt(startedAt: DateTime | null) {
    const newSubmitData: ScheduleFormData = {
      id: schedule?.id || undefined,
      year: null,
      yearInterval: 1,
      month: null,
      monthInterval,
      day: startedAt?.day || day, // 下のcreateSubmitDataで不要な場合にnullになる
      dayInterval,
      wday: null,
      wdayInterval,
      hour:
        !props.isAllday && typeof startedAt?.hour === 'number'
          ? startedAt?.hour
          : 11,
      hourInterval: 0,
      minute: startedAt?.minute ?? 0,
      minuteInterval: 0,
      exceptions: null,
    };
    const newWdays = [...wday];
    if (startedAt) {
      const addWday = startedAt?.weekday === 7 ? 0 : startedAt.weekday;
      if (!newWdays.includes(addWday) && !props.isWeeklySingleSelect) {
        newWdays.push(addWday);
      }
    }
    props.onSubmit(createSubmitData(newSubmitData, newWdays), props.index);
  }

  function onChange(e: SyntheticEvent<HTMLInputElement>) {
    const newSubmitData: ScheduleFormData = generateScheduleFormData();
    const newWdays = [...wday];

    const { name, value } = e.currentTarget;
    switch (name) {
      case 'repeatUnit':
        switch (value) {
          case 'monthly':
            newSubmitData.month = null;
            newSubmitData.monthInterval = 1;
            newSubmitData.wday = null;
            newSubmitData.wdayInterval = 0;
            newSubmitData.dayInterval = 0;
            if (!day) {
              newSubmitData.day = props.startedAt?.day || 1;
            }
            break;
          case 'weekly':
            newSubmitData.month = null;
            newSubmitData.monthInterval = 1;
            newSubmitData.wdayInterval = 1;
            newSubmitData.day = null;
            newSubmitData.dayInterval = 0;
            break;
          case 'daily':
            newSubmitData.month = null;
            newSubmitData.monthInterval = 1;
            newSubmitData.wday = null;
            newSubmitData.wdayInterval = 0;
            newSubmitData.day = null;
            newSubmitData.dayInterval = 1;
            break;
        }
        break;

      case 'monthInterval':
        newSubmitData.monthInterval = Number(value);
        break;
      case 'wdayInterval':
        newSubmitData.wdayInterval = Number(value);
        break;
      case 'dayInterval':
        newSubmitData.dayInterval = Number(value);
        break;

      case 'day':
        newSubmitData.day = Number(value);
        break;

      default:
        break;
    }

    props.onSubmit(createSubmitData(newSubmitData, newWdays), props.index);
  }

  function onChangeWday(value: number) {
    const newSubmitData: ScheduleFormData = generateScheduleFormData();
    let newWdays = [...wday];

    const newWday = Number(value);
    if (props.isWeeklySingleSelect) {
      newWdays = [newWday];
    } else {
      if (newWdays.findIndex((w) => w === newWday) === -1) {
        newWdays.push(newWday);
      } else {
        newWdays = newWdays.filter((w) => w !== newWday);
      }
    }

    props.onSubmit(createSubmitData(newSubmitData, newWdays), props.index);
  }

  function generateScheduleFormData(): ScheduleFormData {
    return {
      id: schedule?.id || undefined,
      year: null,
      yearInterval: 1,
      month: null,
      monthInterval,
      day,
      dayInterval,
      wday: null,
      wdayInterval,
      hour,
      hourInterval: 0,
      minute,
      minuteInterval: 0,
      exceptions: null,
    };
  }

  function createSubmitData(
    newSubmitData: ScheduleFormData,
    newWdays: number[],
  ) {
    const repeatUnit: string = getRepeatUnit(
      newSubmitData.monthInterval,
      newSubmitData.dayInterval,
      newSubmitData.wdayInterval,
    );
    let data: ScheduleFormData[] = [];
    switch (repeatUnit) {
      case 'monthly':
        data.push({ ...newSubmitData, day: newSubmitData.day });
        break;
      case 'weekly':
        if (newWdays.length === 0) {
          if (props.startedAt) {
            data = [
              {
                ...newSubmitData,
                id:
                  props.schedule.length > 0 ? props.schedule[0].id : undefined,
                wday:
                  props.startedAt?.weekday === 7 ? 0 : props.startedAt?.weekday,
                day: null,
              },
            ];
          } else {
            data = Array.from({ length: 7 }).map((_, wday) => ({
              ...newSubmitData,
              id:
                wday < props.schedule.length
                  ? props.schedule[wday].id
                  : undefined,
              wday,
              day: null,
            }));
          }
        } else {
          data = newWdays.map((wday, index) => ({
            ...newSubmitData,
            id:
              index < props.schedule.length
                ? props.schedule[index].id
                : undefined,
            wday,
            day: null,
          }));
        }
        break;
      default:
        data.push({ ...newSubmitData, day: null });
        break;
    }
    return data;
  }

  return {
    state: {
      repeatUnit,
      monthInterval,
      day,
      dayInterval,
      wday,
      wdayInterval,
      hour,
      minute,
    },
    actions: {
      onChange,
      onChangeStartedAt,
      onChangeWday,
    },
  };
};
